import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Container,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Fade,
  Grow,
  SpeedDial,
  SpeedDialAction,
  SpeedDialIcon,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  // StarCircleOutline as StarCircleOutlineIcon,
  // AlertCircleOutline as AlertCircleOutlineIcon,
  Star as StarIcon,
  SmartToy as RobotIcon,
  Refresh as RefreshIcon,
  Share as ShareIcon,
  Menu as MenuIcon,
  Close as CloseIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useUser } from '../../context/UserContext';
import { theme, spacing, responsiveSpacing, responsiveFontSizes } from '../../theme/theme';
// 导入AstrolabeChart组件及其类型定义
import AstrolabeChart from '../../components/astrology/AstrolabeChart';

const GradientBox = styled(Box)(({ theme: muiTheme }) => ({
  background: `linear-gradient(135deg, ${theme.colors.primary}, ${theme.colors.accent})`,
  padding: muiTheme.spacing(2),
  paddingTop: muiTheme.spacing(2),
  color: 'white',
  textAlign: 'center',
  [muiTheme.breakpoints.up('xs')]: {
    padding: responsiveSpacing.container.xs,
    paddingTop: responsiveSpacing.container.xs,
  },
  [muiTheme.breakpoints.up('sm')]: {
    padding: responsiveSpacing.container.sm,
    paddingTop: responsiveSpacing.container.sm,
  },
  [muiTheme.breakpoints.up('md')]: {
    padding: responsiveSpacing.container.md,
    paddingTop: responsiveSpacing.container.md,
  },
  [muiTheme.breakpoints.up('lg')]: {
    padding: responsiveSpacing.container.lg,
    paddingTop: responsiveSpacing.container.lg,
  },
  [muiTheme.breakpoints.up('xl')]: {
    padding: responsiveSpacing.container.xl,
    paddingTop: responsiveSpacing.container.xl,
  },
}));

const StyledCard = styled(Card)(({ theme: muiTheme }) => ({
  marginTop: muiTheme.spacing(1),
  borderRadius: 16,
  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
  [muiTheme.breakpoints.up('xs')]: {
    marginTop: responsiveSpacing.component.xs,
  },
  [muiTheme.breakpoints.up('sm')]: {
    marginTop: responsiveSpacing.component.sm,
  },
  [muiTheme.breakpoints.up('md')]: {
    marginTop: responsiveSpacing.component.md,
  },
  [muiTheme.breakpoints.up('lg')]: {
    marginTop: responsiveSpacing.component.lg,
  },
  [muiTheme.breakpoints.up('xl')]: {
    marginTop: responsiveSpacing.component.xl,
  },
}));

const EmptyContainer = styled(Box)(({ theme: muiTheme }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: '60vh',
  padding: muiTheme.spacing(2),
  textAlign: 'center',
  [muiTheme.breakpoints.up('xs')]: {
    padding: responsiveSpacing.section.xs,
  },
  [muiTheme.breakpoints.up('sm')]: {
    padding: responsiveSpacing.section.sm,
  },
  [muiTheme.breakpoints.up('md')]: {
    padding: responsiveSpacing.section.md,
  },
  [muiTheme.breakpoints.up('lg')]: {
    padding: responsiveSpacing.section.lg,
  },
  [muiTheme.breakpoints.up('xl')]: {
    padding: responsiveSpacing.section.xl,
  },
}));

const LoadingContainer = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: '60vh',
  textAlign: 'center',
}));



const AstrolabeScreen = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { birthInfo, astrolabe, loading, error, updateBirthInfo, generateAstrolabe } = useUser();
  const [speedDialOpen, setSpeedDialOpen] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' as 'success' | 'error' | 'info' });
  const [fabOpen, setFabOpen] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');

  const handleRegenerateAstrolabe = async () => {
    setConfirmDialog(false);
    if (birthInfo) {
      try {
        await generateAstrolabe(birthInfo);
        setSnackbarMessage('命盘已重新生成');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
      } catch (error) {
        console.error('重新生成命盘失败:', error);
        setSnackbarMessage('重新生成失败，请重试');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      }
    }
  };

  const handleShareAstrolabe = async () => {
    if (!user) {
      setSnackbarMessage('请先登录');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    try {
      // TODO: 实现分享星盘的逻辑
      setSnackbarMessage('星盘数据已复制到剪贴板');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (error) {
      console.error('复制命盘数据失败:', error);
      setSnackbarMessage('复制失败，请重试');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const speedDialActions = [
    {
      icon: <RefreshIcon sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }} />,
      name: '重新生成',
      onClick: () => setConfirmDialog(true),
    },
    {
      icon: <ShareIcon sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }} />,
      name: '分享命盘',
      onClick: handleShareAstrolabe,
    },
    {
      icon: <RobotIcon sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }} />,
      name: 'AI解读',
      onClick: () => navigate('/consultation'),
    },
  ];

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  if (!birthInfo) {
    return (
      <Box sx={{ minHeight: '100vh', backgroundColor: theme.colors.background }}>
        <EmptyContainer>
          <Fade in timeout={800}>
            <Box>
              <StarIcon sx={{ fontSize: responsiveFontSizes.h1, color: theme.colors.placeholder, mb: responsiveSpacing.component }} />
              <Typography variant="h5" sx={{ fontWeight: 'bold', color: theme.colors.text, mb: responsiveSpacing.component, fontSize: responsiveFontSizes.h3 }}>
                需要出生信息
              </Typography>
              <Typography variant="body1" sx={{ color: theme.colors.placeholder, mb: responsiveSpacing.section, lineHeight: 1.6, fontSize: responsiveFontSizes.body1 }}>
                请先设置您的出生信息以生成紫微斗数命盘
              </Typography>
              <Button
                variant="contained"
                onClick={() => navigate('/profile')}
                sx={{
                  borderRadius: 20,
                  backgroundColor: theme.colors.primary,
                  '&:hover': { backgroundColor: theme.colors.primary + 'dd' },
                }}
              >
                设置出生信息
              </Button>
            </Box>
          </Fade>
        </EmptyContainer>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', backgroundColor: theme.colors.background }}>
        <LoadingContainer>
          <CircularProgress size={60} sx={{ color: theme.colors.primary, mb: responsiveSpacing.component }} />
          <Typography variant="h6" sx={{ color: theme.colors.text, fontSize: responsiveFontSizes.h4 }}>
            正在生成命盘...
          </Typography>
        </LoadingContainer>
      </Box>
    );
  }

  if (!astrolabe) {
    return (
      <Box sx={{ minHeight: '100vh', backgroundColor: theme.colors.background }}>
        <EmptyContainer>
          <Fade in timeout={800}>
            <Box>
              <ErrorIcon sx={{ fontSize: responsiveFontSizes.h1, color: theme.colors.placeholder, mb: responsiveSpacing.component }} />
              <Typography variant="h5" sx={{ fontWeight: 'bold', color: theme.colors.text, mb: responsiveSpacing.component, fontSize: responsiveFontSizes.h3 }}>
                无法生成命盘
              </Typography>
              <Typography variant="body1" sx={{ color: theme.colors.placeholder, mb: responsiveSpacing.section, lineHeight: 1.6, fontSize: responsiveFontSizes.body1 }}>
                生成命盘时遇到问题，请重试
              </Typography>
              <Button
                variant="contained"
                onClick={handleRegenerateAstrolabe}
                sx={{
                  borderRadius: 20,
                  backgroundColor: theme.colors.primary,
                  '&:hover': { backgroundColor: theme.colors.primary + 'dd' },
                }}
              >
                重试
              </Button>
            </Box>
          </Fade>
        </EmptyContainer>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: theme.colors.background, position: 'relative', pb: { xs: '80px', sm: 0 } }}>
      {/* Header */}
      <GradientBox>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#FFFFFF', fontSize: responsiveFontSizes.h2 }}>
          您的命盘
        </Typography>
        <Typography variant="h6" sx={{ color: '#F5F5DC', mt: responsiveSpacing.component.xs, fontSize: responsiveFontSizes.h4 }}>
          紫微斗数命盘
        </Typography>
        {birthInfo && (
          <Box sx={{ mt: responsiveSpacing.component.xs, textAlign: 'center' }}>
            <Typography variant="body2" sx={{ color: '#F5F5DC', opacity: 0.9, fontSize: responsiveFontSizes.body2 }}>
              出生: {birthInfo.birthDate} {birthInfo.birthTime || '未知时间'}
            </Typography>
            <Typography variant="body2" sx={{ color: '#F5F5DC', opacity: 0.9, fontSize: responsiveFontSizes.body2 }}>
              地点: {birthInfo.birthLocation || '未知地点'}
            </Typography>
          </Box>
        )}
      </GradientBox>

      {/* Astrolabe Chart */}
      <Container 
        maxWidth="lg" 
        sx={{ 
          py: { xs: responsiveSpacing.container.xs, sm: responsiveSpacing.container.sm, md: responsiveSpacing.container.md, lg: responsiveSpacing.container.lg, xl: responsiveSpacing.container.xl },
          px: { xs: responsiveSpacing.container.xs, sm: responsiveSpacing.container.sm, md: responsiveSpacing.container.md, lg: responsiveSpacing.container.lg, xl: responsiveSpacing.container.xl }
        }}
      >
        <Box sx={{ 
          padding: { xs: responsiveSpacing.component.xs, sm: responsiveSpacing.component.sm, md: responsiveSpacing.component.md, lg: responsiveSpacing.component.lg, xl: responsiveSpacing.component.xl }
        }}>
          <Grow in timeout={800}>
            <Box>
              {birthInfo && (
                <AstrolabeChart 
                  birthData={{
                    birthday: birthInfo.birthDate || '2000-01-01',
                    birthTime: (() => {
                      // Convert birthTime to timeIndex using the same logic as UserContext
                      const convertHourToTimeIndex = (hour: number): number => {
                        if (hour >= 23 || hour < 1) return hour >= 23 ? 12 : 0; // 晚子时(23:00-0:00)=12, 早子时(0:00-1:00)=0
                        if (hour >= 1 && hour < 3) return 1;   // 丑时(1:00-3:00)
                        if (hour >= 3 && hour < 5) return 2;   // 寅时(3:00-5:00)
                        if (hour >= 5 && hour < 7) return 3;   // 卯时(5:00-7:00)
                        if (hour >= 7 && hour < 9) return 4;   // 辰时(7:00-9:00)
                        if (hour >= 9 && hour < 11) return 5;  // 巳时(9:00-11:00)
                        if (hour >= 11 && hour < 13) return 6; // 午时(11:00-13:00)
                        if (hour >= 13 && hour < 15) return 7; // 未时(13:00-15:00)
                        if (hour >= 15 && hour < 17) return 8; // 申时(15:00-17:00)
                        if (hour >= 17 && hour < 19) return 9; // 酉时(17:00-19:00)
                        if (hour >= 19 && hour < 21) return 10; // 戌时(19:00-21:00)
                        if (hour >= 21 && hour < 23) return 11; // 亥时(21:00-23:00)
                        return 6; // 默认午时
                      };
                      
                      if (typeof birthInfo.birthTime === 'string' && birthInfo.birthTime.includes(':')) {
                        const hour = parseInt(birthInfo.birthTime.split(':')[0]);
                        return convertHourToTimeIndex(hour);
                      }
                      return 6; // 默认午时
                    })(),
                    originalBirthTime: birthInfo.birthTime,
                    gender: birthInfo.gender === 'male' ? '男' : '女',
                    birthdayType: birthInfo.isLunar ? 'lunar' : 'solar'
                  }}
                />
              )}
            </Box>
          </Grow>


        </Box>
      </Container>

      {/* Speed Dial FAB */}
      <SpeedDial
        ariaLabel="Astrolabe Actions"
        sx={{
          position: 'fixed',
          bottom: { xs: 80, sm: 16 },
          right: { xs: 12, sm: 16 },
          '& .MuiFab-primary': {
            backgroundColor: theme.colors.primary,
            width: { xs: 48, sm: 56 },
            height: { xs: 48, sm: 56 },
            '&:hover': {
              backgroundColor: theme.colors.primary + 'dd',
            },
          },
        }}
        icon={<SpeedDialIcon icon={<MenuIcon sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }} />} openIcon={<CloseIcon sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }} />} />}
        onClose={() => setFabOpen(false)}
        onOpen={() => setFabOpen(true)}
        open={fabOpen}
      >
        {speedDialActions.map((action) => (
          <SpeedDialAction
            key={action.name}
            icon={action.icon}
            tooltipTitle={action.name}
            tooltipOpen
            onClick={() => {
              setFabOpen(false);
              action.onClick();
            }}
            sx={{
              '& .MuiFab-primary': {
                backgroundColor: theme.colors.accent,
                width: { xs: 40, sm: 48 },
                height: { xs: 40, sm: 48 },
                '&:hover': {
                  backgroundColor: theme.colors.accent + 'dd',
                },
              },
              '& .MuiSpeedDialAction-staticTooltipLabel': {
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
              },
            }}
          />
        ))}
      </SpeedDial>

      {/* Regenerate Confirmation Dialog */}
      <Dialog open={confirmDialog} onClose={() => setConfirmDialog(false)}>
        <DialogTitle>重新生成命盘</DialogTitle>
        <DialogContent>
          <Typography>
            确定要重新生成命盘吗？这将覆盖当前的命盘数据。
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog(false)}>取消</Button>
          <Button onClick={handleRegenerateAstrolabe} variant="contained" color="primary">
            重新生成
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for copy feedback */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AstrolabeScreen;